import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';

const classifyFleet = (eqptCode) => {
  if (['73W', '73R', '7S7'].includes(eqptCode)) return '-700';
  if (['73H', '738', '7A8'].includes(eqptCode)) return '-800';
  if (['7M8', '7T8', '7U8', '7V8', '7P8', '7X8'].includes(eqptCode)) return 'MAX8';
  return eqptCode || '';
};

export const parseGateSheet = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Parse the data based on expected column positions
        const flightsByDate = {};
        let currentDate = null;

        // Skip header rows and process flight data
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.length === 0) continue;

          // Check column N (index 13) for date
          const dateInColumnN = row[13];
          if (dateInColumnN && isDateValue(dateInColumnN)) {
            currentDate = formatDate(dateInColumnN);
            if (!flightsByDate[currentDate]) {
              flightsByDate[currentDate] = [];
            }
          }

          // Skip if we haven't found a date yet
          if (!currentDate) continue;

          // Adjust these indices based on your actual Excel format
          const depTime = row[7];
          const flightNum = row[8];
          const destination = row[9];
          const aircraftType = row[5];
          const originStatus = row[0];

          if (!depTime || !flightNum || !destination) continue;

          const parsedTime = parseTime(depTime);
          if (!parsedTime) continue;

          flightsByDate[currentDate].push({
            flightNum: String(flightNum).trim(),
            destination: String(destination).trim(),
            depTime: parsedTime,
            aircraft: classifyFleet(String(aircraftType).trim()),
            originator: (!originStatus || String(originStatus).trim() === '') && isEarlyMorning(parsedTime) ? 'Orig' : '',
            destinations: extractDestinations(String(destination)),
            date: currentDate
          });
        }

        // Sort flights by departure time within each date
        Object.keys(flightsByDate).forEach(date => {
          flightsByDate[date].sort((a, b) => a.depTime.localeCompare(b.depTime));
        });

        resolve(flightsByDate);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
};

const parseTime = (timeValue) => {
  if (!timeValue) return null;

  // Handle different time formats
  let timeStr = String(timeValue).trim();

  // If it's already in HH:MM format
  if (/^\d{1,2}:\d{2}$/.test(timeStr)) {
    return timeStr.padStart(5, '0');
  }

  // If it's a decimal (Excel time)
  if (typeof timeValue === 'number') {
    const totalMinutes = Math.round(timeValue * 24 * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  // Try other formats
  const formats = [
    /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i,
    /^(\d{3,4})$/
  ];

  for (const format of formats) {
    const match = timeStr.match(format);
    if (match) {
      if (match[3]) { // AM/PM format
        let hours = parseInt(match[1]);
        const minutes = parseInt(match[2]);
        const isPM = match[3].toUpperCase() === 'PM';

        if (isPM && hours !== 12) hours += 12;
        if (!isPM && hours === 12) hours = 0;

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      } else if (match[0].length >= 3) { // HHMM format
        const hours = Math.floor(parseInt(match[0]) / 100);
        const minutes = parseInt(match[0]) % 100;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
    }
  }

  return null;
};

const isEarlyMorning = (time) => {
  const [hours] = time.split(':').map(Number);
  return hours < 11;
};

const extractDestinations = (destStr) => {
  // Extract all 3-letter airport codes
  const matches = destStr.match(/\b[A-Z]{3}\b/g);
  // Return all destinations - first is initial, rest are thru destinations
  return matches || [];
};

const calculatePullTime = (depTime) => {
  const [hours, minutes] = depTime.split(':').map(Number);
  const totalMinutes = ((hours * 60 + minutes - 35) + 1440) % 1440;
  const pullHours = Math.floor(totalMinutes / 60);
  const pullMinutes = totalMinutes % 60;
  return `${pullHours.toString().padStart(2, '0')}:${pullMinutes.toString().padStart(2, '0')}`;
};

const isDateValue = (value) => {
  if (!value) return false;

  // Check for Excel date serial number (number > 40000)
  if (typeof value === 'number' && value > 40000) {
    return true;
  }

  // Check for common date string patterns
  const datePatterns = [
    /^\d{1,2}\/\d{1,2}\/\d{2,4}$/,  // MM/DD/YYYY or M/D/YY
    /^\d{1,2}-\d{1,2}-\d{2,4}$/,     // MM-DD-YYYY
    /^\d{4}-\d{2}-\d{2}$/,           // YYYY-MM-DD
    /^(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday),?\s+/i  // Day names
  ];

  const strValue = String(value);
  return datePatterns.some(pattern => pattern.test(strValue));
};

const formatDate = (dateValue) => {
  // Handle Excel date serial number
  if (typeof dateValue === 'number' && dateValue > 40000) {
    const excelDate = new Date((dateValue - 25569) * 86400 * 1000);
    // Use UTC methods since the serial number conversion is already in UTC
    const year = excelDate.getUTCFullYear();
    const month = String(excelDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(excelDate.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Handle string dates
  const strValue = String(dateValue);

  // Try to parse various date formats
  const date = new Date(strValue);
  if (!isNaN(date)) {
    // Use local date methods — new Date(string) parses in local time,
    // so getFullYear/getMonth/getDate gives the correct local date.
    // Using toISOString() would convert to UTC and shift the date backward
    // in western US timezones, causing days to merge or skip.
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // If parsing fails, return the original string cleaned up
  return strValue.trim();
};

// Helper function to apply header styling
const applyHeaderStyle = (row, bgColor = 'FF2C3E50', textColor = 'FFFFFFFF') => {
  row.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: bgColor }
    };
    cell.font = {
      bold: true,
      color: { argb: textColor },
      name: 'Arial',
      size: 11
    };
    cell.alignment = {
      horizontal: 'center',
      vertical: 'middle'
    };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FF95A5A6' } },
      left: { style: 'thin', color: { argb: 'FF95A5A6' } },
      bottom: { style: 'thin', color: { argb: 'FF95A5A6' } },
      right: { style: 'thin', color: { argb: 'FF95A5A6' } }
    };
  });
  row.height = 20;
};

// Helper function to style data rows
const applyDataRowStyle = (worksheet, startRow) => {
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber >= startRow) {
      const isEvenRow = (rowNumber - startRow) % 2 === 0;

      row.eachCell((cell) => {
        // Alternating row colors
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: isEvenRow ? 'FFF8F9FA' : 'FFFFFFFF' }
        };
        cell.font = {
          color: { argb: 'FF2C3E50' },
          name: 'Arial',
          size: 10
        };
        cell.alignment = {
          horizontal: 'left',
          vertical: 'middle'
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFDEE2E6' } },
          left: { style: 'thin', color: { argb: 'FFDEE2E6' } },
          bottom: { style: 'thin', color: { argb: 'FFDEE2E6' } },
          right: { style: 'thin', color: { argb: 'FFDEE2E6' } }
        };

        // Center align time columns
        if (typeof cell.value === 'string' && /^\d{2}:\d{2}$/.test(cell.value)) {
          cell.alignment.horizontal = 'center';
        }

        // Highlight "Orig" cells
        if (cell.value === 'Orig') {
          cell.alignment.horizontal = 'center';
          cell.font = { ...cell.font, bold: true };
          cell.fill.fgColor = { argb: 'FFFFF3CD' };
        }
      });
      row.height = 18;
    }
  });
};

export const exportToExcel = async (beltAssignments, configuration, selectedDate) => {
  // Create a new workbook
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Southwest Airlines';
  workbook.created = new Date();

  // =============== SORTED FLIGHTS SHEET ===============
  const sortedSheet = workbook.addWorksheet('SortedFlights');

  // Build headers
  const headers = [];
  const subheaders = [];
  for (let belt = 1; belt <= configuration.activeBelts; belt++) {
    const beltName = configuration.beltNames?.[belt] || `Belt ${belt}`;
    headers.push(beltName, 'Destination', 'Dep', 'Orig', 'A/C', '');
    subheaders.push('Flt', 'Destination', 'Dep', 'Orig', 'A/C', '');
  }

  // Add headers
  const headerRow = sortedSheet.addRow(headers);
  applyHeaderStyle(headerRow);

  const subheaderRow = sortedSheet.addRow(subheaders);
  applyHeaderStyle(subheaderRow, 'FF34495E');

  // Find max flights
  const maxFlights = Math.max(...Object.values(beltAssignments).map(flights => flights.length));

  // Add flight data
  for (let row = 0; row < maxFlights; row++) {
    const rowData = [];
    for (let belt = 1; belt <= configuration.activeBelts; belt++) {
      const flights = beltAssignments[belt] || [];
      const flight = flights[row];

      if (flight) {
        const destinationCell = flight.destination ||
          (Array.isArray(flight.destinations) ? flight.destinations.join(' ') : '');

        rowData.push(
          flight.flightNum,
          destinationCell,
          flight.depTime,
          flight.originator === 'Orig' ? 'Orig' : '',
          flight.aircraft || '',
          ''
        );
      } else {
        rowData.push('', '', '', '', '', '');
      }
    }
    sortedSheet.addRow(rowData);
  }

  // Set column widths
  for (let i = 0; i < configuration.activeBelts * 6; i++) {
    const colNum = i % 6;
    const widths = [10, 20, 8, 6, 6, 2];
    sortedSheet.getColumn(i + 1).width = widths[colNum];
  }

  // Apply data styling
  applyDataRowStyle(sortedSheet, 3);

  // Freeze top 2 rows
  sortedSheet.views = [{ state: 'frozen', ySplit: 2 }];

  // =============== PULL SHEET ===============
  const pullSheet = workbook.addWorksheet('Pull Sheet');

  // Copy headers
  const pullHeaderRow = pullSheet.addRow(headers);
  applyHeaderStyle(pullHeaderRow);

  const pullSubheaderRow = pullSheet.addRow(subheaders);
  applyHeaderStyle(pullSubheaderRow, 'FF34495E');

  // Add flight data with adjusted times
  for (let row = 0; row < maxFlights; row++) {
    const rowData = [];
    for (let belt = 1; belt <= configuration.activeBelts; belt++) {
      const flights = beltAssignments[belt] || [];
      const flight = flights[row];

      if (flight) {
        const destinationCell = flight.destination ||
          (Array.isArray(flight.destinations) ? flight.destinations.join(' ') : '');

        const pullTime = calculatePullTime(flight.depTime);

        rowData.push(
          flight.flightNum,
          destinationCell,
          pullTime,
          flight.originator === 'Orig' ? 'Orig' : '',
          flight.aircraft || '',
          ''
        );
      } else {
        rowData.push('', '', '', '', '', '');
      }
    }
    pullSheet.addRow(rowData);
  }

  // Set column widths
  for (let i = 0; i < configuration.activeBelts * 6; i++) {
    const colNum = i % 6;
    const widths = [10, 20, 8, 6, 6, 2];
    pullSheet.getColumn(i + 1).width = widths[colNum];
  }

  // Apply data styling
  applyDataRowStyle(pullSheet, 3);

  // Freeze top 2 rows
  pullSheet.views = [{ state: 'frozen', ySplit: 2 }];

  // =============== MU ASSIGNMENTS SHEET ===============
  const muSheet = workbook.addWorksheet('MU Assignments');

  // Add Southwest Airlines header
  // Use the selectedDate if provided, otherwise fall back to today's date
  let exportDate;
  if (selectedDate) {
    // Parse as local date to avoid timezone issues
    const [year, month, day] = selectedDate.split('-').map(Number);
    exportDate = new Date(year, month - 1, day);
  } else {
    exportDate = new Date();
  }
  const formattedDate = exportDate.toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  const titleRow = muSheet.addRow(['Southwest Airlines', formattedDate, '', '', '', '', '']);
  titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FF1E3A8A' } };
  titleRow.getCell(2).font = { bold: true, size: 12 };
  titleRow.height = 25;

  muSheet.addRow([]); // Empty row

  const muHeaders = ['Carrier', 'Flight', 'Open Time', 'Close Time', 'STD', 'Lane (MU)', 'Destination'];
  const muHeaderRow = muSheet.addRow(muHeaders);
  applyHeaderStyle(muHeaderRow, 'FF1E3A8A');

  // Collect and sort all MU data
  const muFlights = [];
  Object.entries(beltAssignments).forEach(([belt, flights]) => {
    const mu = configuration.makeupUnits?.[belt] || '';

    // Use override times if enabled, otherwise use belt schedule
    let schedule;
    if (configuration.muAssignmentTimeOverride?.enabled) {
      schedule = {
        openTime: configuration.muAssignmentTimeOverride.openTime,
        closeTime: configuration.muAssignmentTimeOverride.closeTime
      };
    } else {
      schedule = configuration.beltSchedule?.[belt] || { openTime: '03:00', closeTime: '02:59' };
    }

    flights.forEach(flight => {
      let carrier = 'WN';
      let flightNumOnly = flight.flightNum;

      const flightParts = flight.flightNum.match(/^([A-Z]+)\s*(\d+)$/);
      if (flightParts) {
        carrier = flightParts[1];
        flightNumOnly = flightParts[2];
      }

      const destinations = flight.destinations ||
        (flight.destination ? flight.destination.split(/\s+/).filter(d => d.length === 3) : []);

      if (destinations.length > 0) {
        destinations.forEach(dest => {
          muFlights.push([carrier, flightNumOnly, schedule.openTime, schedule.closeTime, flight.depTime, mu, dest]);
        });
      } else {
        muFlights.push([carrier, flightNumOnly, schedule.openTime, schedule.closeTime, flight.depTime, mu, flight.destination || '']);
      }
    });
  });

  // Sort by STD and add to sheet
  muFlights.sort((a, b) => a[4].localeCompare(b[4]));
  muFlights.forEach(flight => muSheet.addRow(flight));

  // Set column widths
  muSheet.getColumn(1).width = 10;
  muSheet.getColumn(2).width = 10;
  muSheet.getColumn(3).width = 12;
  muSheet.getColumn(4).width = 12;
  muSheet.getColumn(5).width = 10;
  muSheet.getColumn(6).width = 15;
  muSheet.getColumn(7).width = 25;

  // Apply data styling
  applyDataRowStyle(muSheet, 4);

  // Freeze header row
  muSheet.views = [{ state: 'frozen', ySplit: 3 }];

  // =============== EXPORT GROUP SHEETS (data-driven) ===============
  const exportGroups = configuration.exportGroups || [];
  exportGroups.forEach((group) => {
    const [startBelt, endBelt] = group.beltRange;
    const groupSheet = workbook.addWorksheet(group.name);

    // Add title
    const groupTitle = groupSheet.addRow([group.title, '', '', '', '', '', '', '']);
    groupSheet.mergeCells('A1:H1');
    groupTitle.getCell(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: group.color }
    };
    groupTitle.getCell(1).font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
    groupTitle.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
    groupTitle.height = 25;

    const groupHeaders = ['Belt', 'Driver', 'Pull Time', 'STD', 'Bookings', 'Flight', 'Dest', 'Gate'];
    const groupHeaderRow = groupSheet.addRow(groupHeaders);
    applyHeaderStyle(groupHeaderRow);

    // Collect and sort flights for this group
    const groupFlights = [];
    for (let belt = startBelt; belt <= Math.min(endBelt, configuration.activeBelts); belt++) {
      const flights = beltAssignments[belt] || [];
      const beltName = configuration.beltNames?.[belt] || `Belt ${belt}`;
      const beltDesignation = beltName.split(' ').pop();

      flights.forEach(flight => {
        const pullTime = calculatePullTime(flight.depTime);

        const destinations = flight.destinations ||
          (flight.destination ? flight.destination.split(/\s+/).filter(d => d.length === 3) : []);
        const destString = destinations.join(' ') || flight.destination || '';

        let flightNumOnly = flight.flightNum;
        const flightParts = flight.flightNum.match(/^([A-Z]+)\s*(\d+)$/);
        if (flightParts) {
          flightNumOnly = flightParts[2];
        }

        groupFlights.push([beltDesignation, '', pullTime, flight.depTime, '', flightNumOnly, destString, '']);
      });
    }

    // Sort by pull time and add to sheet
    groupFlights.sort((a, b) => a[2].localeCompare(b[2]));
    groupFlights.forEach(flight => groupSheet.addRow(flight));

    // Set column widths
    [10, 12, 12, 10, 12, 10, 20, 10].forEach((width, i) => {
      groupSheet.getColumn(i + 1).width = width;
    });

    // Apply data styling
    applyDataRowStyle(groupSheet, 3);

    // Freeze header rows
    groupSheet.views = [{ state: 'frozen', ySplit: 2 }];
  });

  // =============== PIER SUPPORT SHEET ===============
  const pierSupportSheet = workbook.addWorksheet('Pier Support');

  // Add title with date
  const pierSupportTitleRow = pierSupportSheet.addRow(['Pier Support Tag Numbers', formattedDate, '', '']);
  pierSupportTitleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FF1E3A8A' } };
  pierSupportTitleRow.getCell(2).font = { bold: true, size: 12 };
  pierSupportTitleRow.height = 25;

  pierSupportSheet.addRow([]); // Empty row

  // Add headers
  const pierSupportHeaders = ['Flight #', 'Destination', 'Tag #'];
  const pierSupportHeaderRow = pierSupportSheet.addRow(pierSupportHeaders);
  applyHeaderStyle(pierSupportHeaderRow, 'FFF9B612', 'FF000000');

  // Collect all flights with their pier support tag numbers
  const pierSupportFlights = [];
  Object.entries(beltAssignments).forEach(([belt, flights]) => {
    const mu = configuration.makeupUnits?.[belt] || '';
    const tagNumber = configuration.pierSupport?.[mu] || '';

    flights.forEach(flight => {
      // Get destinations array
      const destinations = flight.destinations ||
        (flight.destination ? flight.destination.split(/\s+/).filter(d => d.length === 3) : []);

      // If we have multiple destinations, create a row for each
      if (destinations.length > 0) {
        destinations.forEach(dest => {
          pierSupportFlights.push({
            flightNum: flight.flightNum,
            destination: dest,
            tagNumber: tagNumber
          });
        });
      } else {
        // If no destinations parsed, use the raw destination string
        pierSupportFlights.push({
          flightNum: flight.flightNum,
          destination: flight.destination || '',
          tagNumber: tagNumber
        });
      }
    });
  });

  // Sort by tag number first, then by flight number (numerically)
  pierSupportFlights.sort((a, b) => {
    // First sort by tag number
    const tagCompare = a.tagNumber.localeCompare(b.tagNumber);
    if (tagCompare !== 0) return tagCompare;

    // Then sort by flight number (extract numeric part for proper numeric sorting)
    const getFlightNumber = (flightStr) => {
      const match = flightStr.match(/(\d+)/);
      return match ? parseInt(match[1]) : 0;
    };

    const numA = getFlightNumber(a.flightNum);
    const numB = getFlightNumber(b.flightNum);

    // If numbers are different, sort numerically
    if (numA !== numB) return numA - numB;

    // If numbers are the same, fall back to string comparison (for airline codes)
    return a.flightNum.localeCompare(b.flightNum);
  });

  pierSupportFlights.forEach(flight => {
    pierSupportSheet.addRow([flight.flightNum, flight.destination, flight.tagNumber]);
  });

  // Set column widths
  pierSupportSheet.getColumn(1).width = 15;
  pierSupportSheet.getColumn(2).width = 30;
  pierSupportSheet.getColumn(3).width = 12;

  // Apply data styling
  applyDataRowStyle(pierSupportSheet, 4);

  // Freeze header row
  pierSupportSheet.views = [{ state: 'frozen', ySplit: 3 }];

  // =============== BELT ASSIGNMENTS SHEET ===============
  const beltAssignmentsSheet = workbook.addWorksheet('Belt Assignments');

  // Add title
  const beltAssignmentsTitleRow = beltAssignmentsSheet.addRow(['Belt Assignments by Flight Number', formattedDate, '', '', '', '']);
  beltAssignmentsTitleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FF1E3A8A' } };
  beltAssignmentsTitleRow.getCell(2).font = { bold: true, size: 12 };
  beltAssignmentsTitleRow.height = 25;

  beltAssignmentsSheet.addRow([]); // Empty row

  // Add headers
  const beltAssignmentsHeaders = ['Flight #', 'Destination', 'Belt', 'MU', 'Tag #'];
  const beltAssignmentsHeaderRow = beltAssignmentsSheet.addRow(beltAssignmentsHeaders);
  applyHeaderStyle(beltAssignmentsHeaderRow, 'FF2C3E50');

  // Collect all flights with belt assignments
  const beltAssignmentsFlights = [];
  Object.entries(beltAssignments).forEach(([belt, flights]) => {
    const beltName = configuration.beltNames?.[belt] || `Belt ${belt}`;
    const mu = configuration.makeupUnits?.[belt] || '';
    const tagNumber = configuration.pierSupport?.[mu] || '';

    flights.forEach(flight => {
      const destinationStr = flight.destination ||
        (Array.isArray(flight.destinations) ? flight.destinations.join(' / ') : '');

      beltAssignmentsFlights.push({
        flightNum: flight.flightNum,
        destination: destinationStr,
        belt: beltName,
        mu: mu,
        tagNumber: tagNumber
      });
    });
  });

  // Sort by flight number (numerically) and add to sheet
  beltAssignmentsFlights.sort((a, b) => {
    // Extract numeric part for proper numeric sorting
    const getFlightNumber = (flightStr) => {
      const match = flightStr.match(/(\d+)/);
      return match ? parseInt(match[1]) : 0;
    };

    const numA = getFlightNumber(a.flightNum);
    const numB = getFlightNumber(b.flightNum);

    // If numbers are different, sort numerically
    if (numA !== numB) return numA - numB;

    // If numbers are the same, fall back to string comparison (for airline codes)
    return a.flightNum.localeCompare(b.flightNum);
  });
  beltAssignmentsFlights.forEach(flight => {
    beltAssignmentsSheet.addRow([flight.flightNum, flight.destination, flight.belt, flight.mu, flight.tagNumber]);
  });

  // Set column widths
  beltAssignmentsSheet.getColumn(1).width = 15;
  beltAssignmentsSheet.getColumn(2).width = 30;
  beltAssignmentsSheet.getColumn(3).width = 15;
  beltAssignmentsSheet.getColumn(4).width = 15;
  beltAssignmentsSheet.getColumn(5).width = 10;

  // Apply data styling
  applyDataRowStyle(beltAssignmentsSheet, 4);

  // Freeze header row
  beltAssignmentsSheet.views = [{ state: 'frozen', ySplit: 3 }];

  // =============== BELT ASSIGNMENTS BY DESTINATION SHEET ===============
  const beltAssignmentsByDestSheet = workbook.addWorksheet('Belt Assignments by Dest');

  // Add title
  const beltAssignmentsByDestTitleRow = beltAssignmentsByDestSheet.addRow(['Belt Assignments by Destination', formattedDate, '', '', '', '']);
  beltAssignmentsByDestTitleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FF1E3A8A' } };
  beltAssignmentsByDestTitleRow.getCell(2).font = { bold: true, size: 12 };
  beltAssignmentsByDestTitleRow.height = 25;

  beltAssignmentsByDestSheet.addRow([]); // Empty row

  // Add headers (same as Belt Assignments)
  const beltAssignmentsByDestHeaders = ['Flight #', 'Destination', 'Belt', 'MU', 'Tag #'];
  const beltAssignmentsByDestHeaderRow = beltAssignmentsByDestSheet.addRow(beltAssignmentsByDestHeaders);
  applyHeaderStyle(beltAssignmentsByDestHeaderRow, 'FF16A34A');

  // Use the same data but sort by destination
  const beltAssignmentsByDestFlights = [...beltAssignmentsFlights];
  beltAssignmentsByDestFlights.sort((a, b) => {
    // First sort by destination
    const destCompare = a.destination.localeCompare(b.destination);
    if (destCompare !== 0) return destCompare;

    // Then sort by flight number (numerically)
    const getFlightNumber = (flightStr) => {
      const match = flightStr.match(/(\d+)/);
      return match ? parseInt(match[1]) : 0;
    };

    const numA = getFlightNumber(a.flightNum);
    const numB = getFlightNumber(b.flightNum);

    // If numbers are different, sort numerically
    if (numA !== numB) return numA - numB;

    // If numbers are the same, fall back to string comparison (for airline codes)
    return a.flightNum.localeCompare(b.flightNum);
  });

  beltAssignmentsByDestFlights.forEach(flight => {
    beltAssignmentsByDestSheet.addRow([flight.flightNum, flight.destination, flight.belt, flight.mu, flight.tagNumber]);
  });

  // Set column widths (same as Belt Assignments)
  beltAssignmentsByDestSheet.getColumn(1).width = 15;
  beltAssignmentsByDestSheet.getColumn(2).width = 30;
  beltAssignmentsByDestSheet.getColumn(3).width = 15;
  beltAssignmentsByDestSheet.getColumn(4).width = 15;
  beltAssignmentsByDestSheet.getColumn(5).width = 10;

  // Apply data styling
  applyDataRowStyle(beltAssignmentsByDestSheet, 4);

  // Freeze header row
  beltAssignmentsByDestSheet.views = [{ state: 'frozen', ySplit: 3 }];

  // =============== COLD SORT SHEET ===============
  const coldSortSheet = workbook.addWorksheet('Cold Sort');

  // Add title
  const coldSortTitleRow = coldSortSheet.addRow(['Cold Sort', formattedDate, '', '', '']);
  coldSortTitleRow.getCell(1).font = { bold: true, size: 14, color: { argb: 'FF1E3A8A' } };
  coldSortTitleRow.getCell(2).font = { bold: true, size: 12 };
  coldSortTitleRow.height = 25;

  coldSortSheet.addRow([]); // Empty row

  // Add headers
  const coldSortHeaders = ['Flight Number', 'Destination', 'STD', 'Pull Time', 'Driver'];
  const coldSortHeaderRow = coldSortSheet.addRow(coldSortHeaders);
  applyHeaderStyle(coldSortHeaderRow, 'FF6366F1');

  // Collect all flights with their STD
  const coldSortFlights = [];
  Object.entries(beltAssignments).forEach(([belt, flights]) => {
    flights.forEach(flight => {
      const destinationStr = flight.destination ||
        (Array.isArray(flight.destinations) ? flight.destinations.join(' / ') : '');

      coldSortFlights.push({
        flightNum: flight.flightNum,
        destination: destinationStr,
        std: flight.depTime
      });
    });
  });

  // Sort by STD (departure time)
  coldSortFlights.sort((a, b) => a.std.localeCompare(b.std));

  // Add flights to sheet with empty Pull Time and Driver columns
  coldSortFlights.forEach(flight => {
    coldSortSheet.addRow([flight.flightNum, flight.destination, flight.std, '', '']);
  });

  // Set column widths
  coldSortSheet.getColumn(1).width = 15; // Flight Number
  coldSortSheet.getColumn(2).width = 30; // Destination
  coldSortSheet.getColumn(3).width = 12; // STD
  coldSortSheet.getColumn(4).width = 12; // Pull Time
  coldSortSheet.getColumn(5).width = 15; // Driver

  // Apply data styling
  applyDataRowStyle(coldSortSheet, 4);

  // Freeze header row
  coldSortSheet.views = [{ state: 'frozen', ySplit: 3 }];

  // =============== SAVE FILE ===============
  // Generate filename with date (including day of week)
  const filename = `Sorted_Gate_Sheet_${formattedDate}.xlsx`;

  // Generate buffer and save
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};